import axios from 'axios';
import { IChannelRepository, ChannelListResult, ChannelListParams } from '../../application/ports/IChannelRepository';
import { ChannelFilters } from '../../shared/types/ChannelFilters';
import { ChannelEntity } from '../../domain/entities/Channel';
import { advertizerApiHost } from '../../constants/envs';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';
import { mapApiToEntity } from '../mappers/channels.mapper';

export class ChannelsAxiosRepository implements IChannelRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  private toEntity(raw: any): ChannelEntity { return mapApiToEntity(raw); }

  // Массивы уходят строкой через запятую: спека объявляет style=form, explode=false.
  // Передавать массив нельзя — axios отправит categories[]=a&categories[]=b.
  private toQueryParams(params: Partial<ChannelListParams>) {
    const query: Record<string, string | number> = {};
    if (params.page !== undefined) query.page = params.page;
    if (params.perPage !== undefined) query.perPage = params.perPage;
    if (params.search) query.search = params.search;
    if (params.categories?.length) query.categories = params.categories.join(',');
    if (params.types?.length) query.types = params.types.join(',');
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortOrder) query.sortOrder = params.sortOrder;
    return query;
  }

  async list(params: ChannelListParams): Promise<ChannelListResult> {
    const headers = await this.authHeaders();
    const res = await axios.get(advertizerApiHost + '/api/channels', { params: this.toQueryParams(params), headers });
    return { items: res.data.channels.map((c: any) => this.toEntity(c)), page: res.data.pagination.page, perPage: res.data.pagination.perPage, total: res.data.pagination.total };
  }

  // Фильтры обязаны уходить в ОБА запроса, иначе «Выбрать все» выберет каналы вне фильтра.
  async listAll(filters: ChannelFilters): Promise<ChannelEntity[]> {
    const first = await this.list({ page: 1, perPage: 1, ...filters });
    const total = first.total || 0;
    if (total <= 1) return first.items;
    const full = await this.list({ page: 1, perPage: total || 1000, ...filters });
    return full.items;
  }

  async count(filters: ChannelFilters): Promise<number> {
    const res = await this.list({ page: 1, perPage: 1, ...filters });
    return res.total;
  }

  async get(uuid: string): Promise<ChannelEntity | null> {
    const headers = await this.authHeaders();
    const res = await axios.get(advertizerApiHost + '/api/channels/' + uuid, { headers });
    return this.toEntity(res.data.channel);
  }

  async create(input: { link: string; title?: string; type: string; isPrivate: boolean; minimalSendingInterval: number; sendingStartTime: string; sendingEndTime: string }): Promise<ChannelEntity> {
    const headers = await this.authHeaders();
    const res = await axios.post(advertizerApiHost + '/api/channels', input, { headers });
    return this.toEntity(res.data.channel);
  }

  async update(uuid: string, input: { link?: string; title?: string; type?: string; isPrivate?: boolean; minimalSendingInterval?: number; sendingStartTime?: string; sendingEndTime?: string }): Promise<ChannelEntity> {
    const headers = await this.authHeaders();
    const res = await axios.put(advertizerApiHost + '/api/channels/' + uuid, input, { headers });
    return this.toEntity(res.data.channel);
  }

  async delete(uuid: string): Promise<void> {
    const headers = await this.authHeaders();
    await axios.delete(advertizerApiHost + '/api/channels/' + uuid, { headers });
  }
}
